/**
 * Tenant auth store — session-based login for tenant portal.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient, fetchCsrfCookie } from '@/services/apiClient.js'

export const useTenantAuthStore = defineStore('tenantAuth', () => {
  const storageKey = 'ayawin_tenant_slug'
  const user = ref(null)
  const tenant = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const blockedStatus = ref(null)   // 'suspended' | 'overdue' | null
  const blockedMessage = ref(null)

  const isAuthenticated = computed(() => !!user.value)

  function normalizeTenantSlug(value) {
    if (!value) return ''

    return String(value)
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .replace(/\.(ayawin\.test|ayawin\.tn|aya-win\.com)$/i, '')
      .replace(/[^a-z0-9-]/g, '')
  }

  // Listen for server-side status events fired by apiClient
  if (typeof window !== 'undefined') {
    window.addEventListener('tenant:blocked', (e) => {
      blockedStatus.value = e.detail?.status || 'suspended'
      blockedMessage.value = e.detail?.message || null
    })
    window.addEventListener('tenant:overdue', () => {
      if (blockedStatus.value !== 'suspended') {
        blockedStatus.value = 'overdue'
      }
    })
  }

  function setTenantSlug(slug) {
    const normalizedSlug = normalizeTenantSlug(slug)

    try {
      if (normalizedSlug) {
        window.localStorage.setItem(storageKey, normalizedSlug)
      } else {
        window.localStorage.removeItem(storageKey)
      }
    } catch {
      // ignore storage failures
    }
  }

  async function login(email, password, tenantSlug = null) {
    loading.value = true
    error.value = null
    try {
      if (tenantSlug !== null) {
        setTenantSlug(tenantSlug)
      }

      await fetchCsrfCookie()
      // Send tenant slug both in localStorage (for header) AND in POST body for redundancy
      const data = await apiClient.post('/login', { email, password, workspace: tenantSlug })
      user.value = data.user
      tenant.value = data.tenant || null
      setTenantSlug(data.tenant?.slug || tenant.value?.slug || null)
      return data
    } catch (e) {
      error.value = e.status === 422 ? e.errors : e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await apiClient.post('/logout')
    } finally {
      user.value = null
      tenant.value = null
      setTenantSlug(null)
    }
  }

  async function fetchUser() {
    try {
      const data = await apiClient.get('/profile')
      user.value = data.user || data
      tenant.value = data.tenant || null
      setTenantSlug(data.tenant?.slug || tenant.value?.slug || null)
    } catch {
      user.value = null
      tenant.value = null
    }
  }

  return { user, tenant, loading, error, isAuthenticated, blockedStatus, blockedMessage, login, logout, fetchUser, setTenantSlug, normalizeTenantSlug }
})
